import type { CSSProperties } from 'react';

/**
 * THE SHAPE OF AN OPTION GRID IS THE OPTION COUNT'S TO DECIDE.
 *
 * A field of option tiles is laid out from how many options there are, not
 * from how much width happens to be going spare. `auto-fit` answers the
 * second question: it takes as many tracks as clear a minimum, so four
 * options on a laptop became one row of narrow tiles with the block space
 * below them left to the orb, and the grid never asked for a share of it.
 *
 * The shape here is the SQUAREST one that holds the options — `ceil(sqrt(n))`
 * columns, so four are quadrants, six are 3x2 and nine are 3x3. A square
 * grid is the arrangement that turns a given area into the largest tiles,
 * which is the whole point of the surface the learner has to act on (design
 * system § 14).
 *
 * ROWS COST WHAT THE ORB CAN AFFORD TO GIVE, and it can. Seven options are
 * three rows here rather than the two a width-derived grid produced, and the
 * height that takes comes from `--audio-orb-size`, whose `clamp()` is already
 * written to yield on a short screen (NoteIdentificationRenderer.module.css).
 * One rule at every height is worth more than a row count tuned per viewport
 * band.
 *
 * Below the touch band this is not consulted: the 360px viewport keeps the
 * `auto-fit` floor that guarantees it a single column (FR-047, SC-015,
 * research D-009), because there the tile minimum, not the count, is what the
 * layout has to respect.
 */
export function optionGridStyle(count: number): CSSProperties {
  return { '--option-columns': Math.max(1, Math.ceil(Math.sqrt(count))) } as CSSProperties;
}
